import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  PageHeader,
  Typography,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  Collapse,
  Button,
  Space,
  Tooltip,
} from 'antd';
import {
  TeamOutlined,
  BarChartOutlined,
  WifiOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  handleApiError,
  MyBreadcrumb,
  accessControls,
} from 'utils';
import { branchnetworkStatsService } from 'services/branchnetwork';
import { wifiusersSuperAdminSearchService } from 'services/wifiusers';
import Auth from 'auth';
import Papa from 'papaparse';

const { Panel } = Collapse;
const { Title } = Typography;

export default function BranchnetworkStats() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [totalNetworks, setTotalNetworks] = useState(0);
  const [totalClients, setTotalClients] = useState(0);
  const [exportingWifiUsers, setExportingWifiUsers] = useState({});
  const [exportingAllClients, setExportingAllClients] = useState(false);
  const [exportingAllWifiUsers, setExportingAllWifiUsers] = useState(false);

  useEffect(() => {
    loadData();
    loadPermissions();
  }, []);

  const loadPermissions = () => {
    accessControls.fetch().then((permissions) => {
      setPermissions(permissions);
    });
  };

  const loadData = () => {
    setIsLoading(true);
    branchnetworkStatsService()
      .then((response) => {
        setIsLoading(false);
        if (response.data.items && response.data.items.length > 0) {
          setDataSource(response.data.items);
          const totalClientsCount = response.data.items.reduce(
            (sum, network) => sum + network.clientCount,
            0,
          );
          setTotalNetworks(response.data.items.length);
          setTotalClients(totalClientsCount);
        } else {
          setDataSource([]);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  const isSuperAdmin = Number(Auth.getRole()) === 10;

  const downloadCsv = (rows, filename) => {
    const csv = Papa.unparse(rows);
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportClientsToCSV = (network) => {
    if (!network || !network.clients || network.clients.length === 0) {
      handleApiError({ message: 'No clients to export for this branch network' });
      return;
    }

    const clients = network.clients.map((client) => ({
      'Company Name': client.customername || '',
      VLAN: client.vlan || '',
      Bandwidth: client.bandwidth?.description || client.bandwidth?.name || '',
    }));

    const networkName = `${network.company?.company_name || 'Network'}-${network.branch?.branch_name || 'Unknown'}`.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    downloadCsv(
      clients,
      `branch-clients-${networkName}-${new Date().toISOString().split('T')[0]}.csv`,
    );
  };

  const resolveWifiBranchName = (user) => {
    if (user.branchcustomer?.customername) {
      return user.branchcustomer.customername;
    }
    if (user.wifigroup?.branchcustomer?.customername) {
      return user.wifigroup.branchcustomer.customername;
    }
    if (user.branchnetwork_name) {
      return user.branchnetwork_name;
    }
    if (user.company?.company_name && user.branch?.branch_name) {
      return `${user.company.company_name} - ${user.branch.branch_name}`;
    }
    return '';
  };

  const exportAllBranchClients = () => {
    setExportingAllClients(true);
    try {
      const rows = [];
      dataSource.forEach((network) => {
        const companyName = network.company?.company_name || '';
        const branchName = network.branch?.branch_name || '';
        const networkLabel = `${companyName} - ${branchName}`.trim();
        (network.clients || []).forEach((client) => {
          rows.push({
            'Branch Network': networkLabel,
            Company: companyName,
            Branch: branchName,
            'Network Reference ID': network.reference_id || '',
            'Customer Name': client.customername || '',
            'Account Number': client.accountnumber || '',
            VLAN: client.vlan || '',
            Bandwidth:
              client.bandwidth?.description || client.bandwidth?.name || '',
            'Client ID': client._id || '',
          });
        });
      });

      if (rows.length === 0) {
        handleApiError({ message: 'No branch clients found to export' });
        return;
      }

      downloadCsv(
        rows,
        `all-branch-clients-${new Date().toISOString().split('T')[0]}.csv`,
      );
    } finally {
      setExportingAllClients(false);
    }
  };

  const fetchAllWifiUsers = async (extraParams = {}) => {
    const pageSize = 1000;
    let page = 1;
    let allDocs = [];
    let totalPages = 1;

    do {
      const response = await wifiusersSuperAdminSearchService('', {
        page,
        limit: pageSize,
        ...extraParams,
      });
      const result = response.data.result || {};
      const docs = result.docs || [];
      allDocs = allDocs.concat(docs);
      totalPages = result.totalPages || 1;
      page += 1;
    } while (page <= totalPages);

    return allDocs;
  };

  const exportAllWifiUsers = async () => {
    setExportingAllWifiUsers(true);
    try {
      const docs = await fetchAllWifiUsers();
      if (!docs.length) {
        handleApiError({ message: 'No wireless users found to export' });
        return;
      }

      const wifiUsers = docs.map((user) => ({
        Name: user.fullname || '',
        Username: user.username || '',
        Email: user.username || '',
        Password: user.password || '',
        Status: user.status || '',
        'User Count': user.usercount != null ? user.usercount : '',
        'Wi-Fi Group': user.wifigroup?.wifigroupname || '',
        'Branch Client': resolveWifiBranchName(user),
        'Branch Network': user.branchnetwork_name || '',
        Company: user.company?.company_name || '',
        Branch: user.branch?.branch_name || '',
        'Reference ID': user.reference_id || '',
        'User ID': user._id || '',
      }));

      downloadCsv(
        wifiUsers,
        `all-wireless-users-${new Date().toISOString().split('T')[0]}.csv`,
      );
    } catch (error) {
      handleApiError(error);
    } finally {
      setExportingAllWifiUsers(false);
    }
  };

  const exportWifiUsersToCSV = async (network) => {
    if (!network || !network._id) {
      handleApiError({ message: 'Invalid branch network' });
      return;
    }

    setExportingWifiUsers((prev) => ({ ...prev, [network._id]: true }));

    try {
      const docs = await fetchAllWifiUsers({ branchnetworkId: network._id });

      if (!docs.length) {
        handleApiError({ message: 'No Wi-Fi users found for this branch network' });
        return;
      }

      const wifiUsers = docs.map((user) => ({
        Name: user.fullname || user.username || '',
        Email: user.username || '',
        Branch: resolveWifiBranchName(user),
        Passwords: user.password || '',
      }));

      const networkName = `${network.company?.company_name || 'Network'}-${network.branch?.branch_name || 'Unknown'}`.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      downloadCsv(
        wifiUsers,
        `wifi-users-${networkName}-${new Date().toISOString().split('T')[0]}.csv`,
      );
    } catch (error) {
      handleApiError(error);
    } finally {
      setExportingWifiUsers((prev) => ({ ...prev, [network._id]: false }));
    }
  };

  const clientColumns = [
    {
      title: 'Customer Name',
      dataIndex: 'customername',
      key: 'customername',
      sorter: (a, b) => {
        if (!a.customername || !b.customername) return 0;
        return a.customername.localeCompare(b.customername);
      },
      render: (text, record) => (
        <a
          onClick={(e) => {
            e.preventDefault();
            history.push(`/branchcustomer/update/${record._id}`);
          }}
          style={{ cursor: 'pointer' }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'Account Number',
      dataIndex: 'accountnumber',
      key: 'accountnumber',
      sorter: (a, b) => {
        if (!a.accountnumber || !b.accountnumber) return 0;
        return a.accountnumber.localeCompare(b.accountnumber);
      },
    },
    {
      title: 'VLAN',
      dataIndex: 'vlan',
      key: 'vlan',
      render: (vlan) => vlan || '-',
    },
    {
      title: 'Bandwidth',
      dataIndex: 'bandwidth',
      key: 'bandwidth',
      render: (bandwidth) => {
        if (bandwidth && bandwidth.name) {
          return (
            <Tag color="blue">
              {bandwidth.name}
              {bandwidth.description ? ` - ${bandwidth.description}` : ''}
            </Tag>
          );
        }
        return <Tag color="default">Not Assigned</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Tooltip title="Show Wi-Fi Users">
            <Button
              type="primary"
              shape="circle"
              icon={<WifiOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/branchnetwork/stats/wifiusers/${record._id}`);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {accessControls.can(permissions, 'Client.read:any') && (
        <>
          <MyBreadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Super Admin', path: '/super-admin' },
              { label: 'Branch Network Stats', path: '' },
            ]}
          />
          <PageHeader
            ghost={false}
            avatar={{ icon: <BarChartOutlined /> }}
            title="Branch Network Statistics"
            subTitle="View client counts and bandwidth assignments per Branch Network"
            extra={
              isSuperAdmin ? (
                <Space>
                  <Button
                    icon={<DownloadOutlined />}
                    loading={exportingAllClients}
                    onClick={exportAllBranchClients}
                  >
                    Export All Clients
                  </Button>
                  <Button
                    icon={<WifiOutlined />}
                    loading={exportingAllWifiUsers}
                    onClick={exportAllWifiUsers}
                  >
                    Export All Wi-Fi Users
                  </Button>
                  <Button
                    type="link"
                    onClick={() => history.push('/super-admin/data-export')}
                  >
                    Data Export
                  </Button>
                </Space>
              ) : null
            }
          />
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total Branch Networks"
                  value={totalNetworks}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                <Statistic
                  title="Total Branch Clients"
                  value={totalClients}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
          </Row>
          <Card>
            <Title level={4}>Branch Networks and Clients</Title>
            <Collapse
              defaultActiveKey={[]}
              expandIconPosition="right"
              style={{ marginTop: 16 }}
            >
              {dataSource.map((network) => (
                <Panel
                  key={network._id}
                  header={
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <a
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(
                              `/branchnetwork/update/${network._id}`,
                            );
                          }}
                          style={{
                            cursor: 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                          }}
                        >
                          {network.company?.company_name || 'N/A'} -{' '}
                          {network.branch?.branch_name || 'N/A'}
                        </a>
                        {network.reference_id && (
                          <Tag style={{ marginLeft: 8 }}>
                            {network.reference_id}
                          </Tag>
                        )}
                        {network.subnet_range_from &&
                          network.subnet_range_to && (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                              {network.subnet_range_from} -{' '}
                              {network.subnet_range_to}
                            </Tag>
                          )}
                      </div>
                      <Tag color="blue" style={{ marginRight: 0 }}>
                        {network.clientCount} Client
                        {network.clientCount !== 1 ? 's' : ''}
                      </Tag>
                    </div>
                  }
                >
                  {network.clients && network.clients.length > 0 ? (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 16px',
                          marginBottom: 16,
                          backgroundColor: '#fafafa',
                          borderRadius: '4px',
                          border: '1px solid #f0f0f0',
                        }}
                      >
                        <Typography.Text strong style={{ fontSize: '16px' }}>
                          {network.clientCount} Client
                          {network.clientCount !== 1 ? 's' : ''}
                        </Typography.Text>
                        {isSuperAdmin && (
                          <Space>
                            <Button
                              type="primary"
                              icon={<DownloadOutlined />}
                              onClick={() => exportClientsToCSV(network)}
                            >
                              Export Clients
                            </Button>
                            <Button
                              type="default"
                              icon={<WifiOutlined />}
                              loading={exportingWifiUsers[network._id]}
                              onClick={() => exportWifiUsersToCSV(network)}
                            >
                              Export Wi-Fi Users
                            </Button>
                          </Space>
                        )}
                      </div>
                      <Table
                        dataSource={network.clients}
                        columns={clientColumns}
                        rowKey="_id"
                        pagination={false}
                        size="small"
                      />
                    </div>
                  ) : (
                    <p>No clients assigned to this Branch Network</p>
                  )}
                </Panel>
              ))}
            </Collapse>
          </Card>
        </>
      )}
    </div>
  );
}
